import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import clientApi, { clientToken } from "@/lib/clientApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ClientAuth({ mode }) {
  const isRegister = mode === "register";
  const navigate = useNavigate();
  const location = useLocation();
  const prefillEmail = new URLSearchParams(location.search).get("email") || "";
  const [form, setForm] = useState({ name: "", email: prefillEmail, password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = isRegister ? "/clients/register" : "/clients/login";
      const body = isRegister ? form : { email: form.email, password: form.password };
      const { data } = await clientApi.post(path, body);
      clientToken.set(data.token);
      toast.success(isRegister ? "Account created!" : "Welcome back!");
      navigate("/client");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const field = "h-11 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50";

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <div className="w-full max-w-sm animate-fade-up">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-500 hover:text-slate-300 text-sm mb-6"><ArrowLeft className="h-4 w-4" /> Back to home</button>
        <div className="flex items-center gap-3 mb-7">
          <img src="/pitcrew-logo-horizontal.png" alt="Pitcrew" className="h-9 object-contain" data-testid="client-auth-logo" />
        </div>
        <h1 className="font-head font-bold text-2xl text-slate-50">{isRegister ? "Track your repair" : "Welcome back"}</h1>
        <p className="text-slate-500 text-sm mt-1 mb-7">{isRegister ? "Create an account to see mechanics and follow your request." : "Sign in to view your requests."}</p>
        <form onSubmit={submit} className="space-y-4">
          {isRegister && (
            <div className="space-y-1.5"><Label className="text-slate-300">Your name</Label><Input data-testid="client-name-input" value={form.name} onChange={set("name")} placeholder="Nina Client" className={field} /></div>
          )}
          <div className="space-y-1.5"><Label className="text-slate-300">Email</Label><Input data-testid="client-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" className={field} /></div>
          <div className="space-y-1.5"><Label className="text-slate-300">Password</Label><Input data-testid="client-password-input" type="password" required value={form.password} onChange={set("password")} placeholder="••••••••" className={field} /></div>
          <Button type="submit" disabled={busy} data-testid="client-submit-button" className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{isRegister ? "Create account" : "Sign in"}
          </Button>
        </form>
        <p className="text-sm text-slate-500 text-center mt-7">
          {isRegister ? "Already have an account? " : "New to Pitcrew? "}
          <Link to={isRegister ? "/client/login" : "/client/register"} className="text-amber-400 hover:text-amber-300 font-medium">
            {isRegister ? "Sign in" : "Create an account"}
          </Link>
        </p>
      </div>
    </div>
  );
}
